import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Action,
  ActionStatus,
  MutationSubscribeArgs,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getMemberKey } from '../../utils/keys/member.key.js'
import { getPostKey } from '../../utils/keys/post.key.js'
import {
  bumpFollowStateVersion,
  followStateUpdater,
} from '../../utils/post-follow.util.js'
import { invalidateSpaceQueries } from '../../utils/space.utils.js'
import { updatePostCaches } from '../cache/useCachedPost.js'
import { useSpaceUtil } from '../utils/useSpaceUtil.js'

type SubscribeArgs = MutationSubscribeArgs & {
  action: 'subscribe' | 'unsubscribe'
  publisherType: 'space' | 'post' | 'member'
}

export const useUpdateSubscription = (options?: {
  useMutationOptions?: UseMutationOptions<Action, ClientError, SubscribeArgs>
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const spaceUtils = useSpaceUtil()

  return useMutation<Action, ClientError, SubscribeArgs>(
    ({ action, publisherId, level }) =>
      action === 'subscribe'
        ? client.mutation({
            name: 'subscribe',
            args: { fields: 'all', variables: { publisherId, level } },
          })
        : client.mutation({
            name: 'unsubscribe',
            args: { fields: 'all', variables: { publisherId } },
          }),
    {
      onMutate: ({ publisherType, publisherId }) => {
        if (publisherType === 'post') {
          bumpFollowStateVersion(publisherId)
        }
      },
      onSettled: (
        data,
        error,
        { action, publisherType, publisherId, level },
      ) => {
        switch (publisherType) {
          case 'space': {
            invalidateSpaceQueries({
              queryClient,
              spaceId: publisherId,
              spaceUtils,
            })
            return undefined
          }

          case 'post': {
            if (!error && data?.status === ActionStatus.succeeded) {
              // The server remembers an unfollow, so a later reply won't follow the post again.
              updatePostCaches(
                publisherId,
                followStateUpdater(
                  action === 'subscribe',
                  level,
                  action === 'unsubscribe',
                ),
                queryClient,
              )
            }
            const postKey = getPostKey({ variables: { id: publisherId } })
            queryClient.invalidateQueries(postKey)
            return undefined
          }

          case 'member': {
            const memberKey = getMemberKey({ variables: { id: publisherId } })
            queryClient.invalidateQueries(memberKey)
            return undefined
          }

          default: {
            const _exhaustiveCheck: never = publisherType
            return _exhaustiveCheck
          }
        }
      },
      ...useMutationOptions,
    },
  )
}
